import { apiClient } from '@/api/client';
import type { AuthenticateRequest, AuthenticateResponse, UpdateUserRequest, UpdateUserResponse, BackendUser, RefreshTokenResponse, SendEmailResponse, SwitchRoleRequest, SwitchRoleResponse } from '@/types/api';
import { getRefreshToken } from '@/utils/storage';

/**
 * POST /api/auth/authenticate
 * Authenticate user with Firebase credentials
 * Creates new user if doesn't exist, returns existing user otherwise
 */
export const authenticateUser = async (
  request: AuthenticateRequest
): Promise<AuthenticateResponse['data']> => {
  const response = await apiClient.post<AuthenticateResponse>(
    '/auth/authenticate',
    request
  );

  if (!response.data.success) {
    throw new Error('Authentication failed');
  }

  return response.data.data;
};

/**
 * PUT /api/auth/user
 * Update user fields (emailVerified, lastLoginAt, profile flags)
 * Requires JWT authentication
 */
export const updateUser = async (
  updates: UpdateUserRequest
): Promise<BackendUser> => {
  const response = await apiClient.put<UpdateUserResponse>(
    '/auth/user',
    updates
  );

  if (!response.data.success) {
    throw new Error('Failed to update user');
  }

  return response.data.data;
};

/**
 * POST /api/auth/refresh
 * Refresh access token using refresh token
 * Returns new access token and refresh token (token rotation)
 * Note: Old tokens become invalid after refresh
 */
export const refreshAccessToken = async (): Promise<RefreshTokenResponse['data']> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await apiClient.post<RefreshTokenResponse>(
    '/auth/refresh',
    { refreshToken }
  );

  if (!response.data.success) {
    throw new Error('Failed to refresh token');
  }

  return response.data.data;
};

/**
 * POST /api/auth/send-verification-email
 * Send email verification email via Brevo
 * Used for new signups and resending verification emails
 */
export const sendVerificationEmail = async (email: string): Promise<void> => {
  const response = await apiClient.post<SendEmailResponse>(
    '/auth/send-verification-email',
    { email }
  );

  if (!response.data.success) {
    throw new Error('Failed to send verification email');
  }
};

/**
 * POST /api/auth/send-password-reset
 * Send password reset OTP via Brevo
 * Always returns 200 OK regardless of user existence (prevents email enumeration)
 */
export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  const response = await apiClient.post<SendEmailResponse>(
    '/auth/send-password-reset',
    { email }
  );

  if (!response.data.success) {
    throw new Error('Failed to send password reset email');
  }
};

/**
 * POST /api/auth/verify-otp
 * Verify email with 6-digit OTP
 * Returns JWT and refresh tokens on successful verification
 */
export const verifyOTP = async (email: string, otp: string): Promise<AuthenticateResponse['data']> => {
  const response = await apiClient.post<AuthenticateResponse>(
    '/auth/verify-otp',
    { email, otp }
  );

  if (!response.data.success) {
    throw new Error('OTP verification failed');
  }

  return response.data.data;
};

/**
 * POST /api/auth/resend-verification-otp
 * Resend email verification OTP
 */
export const resendVerificationOTP = async (email: string): Promise<void> => {
  const response = await apiClient.post<SendEmailResponse>(
    '/auth/resend-verification-otp',
    { email }
  );

  if (!response.data.success) {
    throw new Error('Failed to resend verification OTP');
  }
};

/**
 * POST /api/auth/reset-password
 * Reset password with OTP and new password
 */
export const resetPasswordWithOTP = async (email: string, otp: string, newPassword: string): Promise<void> => {
  const response = await apiClient.post<SendEmailResponse>(
    '/auth/reset-password',
    { email, otp, newPassword }
  );

  if (!response.data.success) {
    throw new Error('Password reset failed');
  }
};

/**
 * POST /api/auth/switch-role
 * Switch the authenticated user's active role
 * Returns new JWT tokens and the selected role's profile
 */
export const switchRole = async (newRole: 'tutor' | 'parent'): Promise<SwitchRoleResponse['data']> => {
  const response = await apiClient.post<SwitchRoleResponse>(
    '/auth/switch-role',
    { newRole } as SwitchRoleRequest
  );

  if (!response.data.success) {
    throw new Error('Failed to switch role');
  }

  return response.data.data;
};

/**
 * POST /api/auth/logout
 * Invalidates all issued tokens server-side by incrementing tokenVersion
 */
export const logout = async (): Promise<void> => {
  await apiClient.post('/auth/logout');
};

export const authApi = {
  authenticateUser,
  updateUser,
  refreshAccessToken,
  sendVerificationEmail,
  sendPasswordResetEmail,
  verifyOTP,
  resendVerificationOTP,
  resetPasswordWithOTP,
  switchRole,
  logout,
};
